import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Typography,
  App,
  Space,
  message as antMessage,
  Alert,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import {
  goodsReceiptNoteService,
  type ReceivablePOLineDTO,
  type CreateGRNDTO,
} from '@/services/goodsReceiptNote.service';
import { useWarehouses } from '@/hooks/useInventory';
import inventoryService, { type Item } from '@/services/inventory.service';
import { inventoryUomFromSelectOption, ItemSelect } from '@/components/inventory/ItemSelect';
import { useUsers } from '@/hooks/useUsers';
import { useProjectsForSelection } from '@/hooks/useProject';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { poMilestoneService } from '@/services/poMilestone.service';
import { PO_PAYMENT_TERM_MILESTONE } from '@/constants/poMilestoneTypes';
import { headerProjectLabel, projectLabel, hasMultipleLineProjects } from '@/utils/projectDisplay';
import { formatUserRecord } from '@/utils/userDisplay';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import { GrnActualWeightInput, expectedReceiptWeight } from '@/components/procurement/GrnWeightVariance';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { grnLineNeedsHeat, grnLineNeedsMtc, grnTraceItemCode } from '@/utils/grnCriticalReceipt';

type HeatDraftRow = {
  heat_number: string;
  dimension: string;
  quantity: number;
  mtc_document_id?: string | null;
};

function stockIdForCreateLine(
  line: ReceivablePOLineDTO,
  stockItemByLine: Record<string, string | null>
): string | null {
  if (line.item_type !== 'Non-Stock') return null;
  if (Object.prototype.hasOwnProperty.call(stockItemByLine, line.id)) {
    return stockItemByLine[line.id] || null;
  }
  return line.default_stock_item_id || null;
}

const { Title, Text } = Typography;

const CreateGRNPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const [poId, setPoId] = useState<string | null>(searchParams.get('po_id'));
  const [milestoneId, setMilestoneId] = useState<string | null>(searchParams.get('milestone_id'));
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  const [lineQuantities, setLineQuantities] = useState<Record<string, { received: number; accepted: number; rejected: number; hold: number }>>({});
  const [heatDraftByPoItem, setHeatDraftByPoItem] = useState<Record<string, HeatDraftRow[]>>({});
  const [stockItemByLine, setStockItemByLine] = useState<Record<string, string | null>>({});
  const [linkedUomByLine, setLinkedUomByLine] = useState<Record<string, string>>({});
  const [actualWeightByLine, setActualWeightByLine] = useState<Record<string, number | null>>({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const { data: receivablePOs = [] } = useQuery({
    queryKey: ['purchase-orders', 'receivable', 'grn'],
    queryFn: () => purchaseOrderService.list({ receivable: true, receipt_channel: 'grn' }),
  });

  const { data: receivableLines = [], isLoading: linesLoading } = useQuery({
    queryKey: ['grn-receivable-lines', poId],
    queryFn: () => goodsReceiptNoteService.getReceivableLines(poId!),
    enabled: !!poId,
  });

  const { data: warehouses = [] } = useWarehouses();
  const { data: users = [] } = useUsers({ is_active: true, include_user_id: user?.id });
  const { data: projects = [] } = useProjectsForSelection();
  const { data: selectedPo } = useQuery({
    queryKey: ['purchase-order', poId],
    queryFn: () => purchaseOrderService.getById(poId!),
    enabled: !!poId,
  });
  useApplyUserDefaultDepartment(form, !selectedPo?.department_id);

  const isMilestonePo = selectedPo?.payment_term_type === PO_PAYMENT_TERM_MILESTONE;

  const { data: milestones = [] } = useQuery({
    queryKey: ['po-milestones', poId],
    queryFn: () => poMilestoneService.listForPo(poId!),
    enabled: !!poId && isMilestonePo,
  });

  const grnMilestones = useMemo(
    () => milestones.filter((m) => m.fulfillment_method === 'GRN'),
    [milestones]
  );

  const selectedMilestone = grnMilestones.find((m) => m.id === milestoneId);
  const warehouseOptions = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: w.warehouse_name || w.warehouse_code })),
    [warehouses]
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: formatUserRecord(u) || u.username || u.id,
      })),
    [users]
  );

  const defaultWarehouseId = useMemo(() => {
    const main = warehouses.find(
      (w) =>
        String(w.warehouse_name || '').toLowerCase() === 'main warehouse' ||
        String(w.warehouse_code || '').toUpperCase() === 'WH-MAIN'
    );
    return main?.id ?? warehouses[0]?.id;
  }, [warehouses]);

  useEffect(() => {
    if (defaultWarehouseId && !form.getFieldValue('warehouse_id')) {
      form.setFieldsValue({ warehouse_id: defaultWarehouseId });
    }
  }, [defaultWarehouseId, form]);

  const selectedLines = useMemo(
    () => receivableLines.filter((l) => selectedLineIds.has(l.id)),
    [receivableLines, selectedLineIds]
  );

  const linkedStockIds = useMemo(() => {
    const ids = new Set<string>();
    for (const line of receivableLines) {
      const id = stockIdForCreateLine(line, stockItemByLine);
      if (id) ids.add(id);
    }
    return [...ids];
  }, [receivableLines, stockItemByLine]);

  const linkedItemQueries = useQueries({
    queries: linkedStockIds.map((id) => ({
      queryKey: ['items', id],
      queryFn: () => inventoryService.getItemById(id),
      staleTime: 60_000,
    })),
  });

  const linkedById = useMemo(() => {
    const map = new Map<string, Item>();
    linkedStockIds.forEach((id, index) => {
      const item = linkedItemQueries[index]?.data;
      if (item) map.set(id, item);
    });
    return map;
  }, [linkedStockIds, linkedItemQueries]);

  const linkedItemFor = (line: ReceivablePOLineDTO) => {
    const id = stockIdForCreateLine(line, stockItemByLine);
    return id ? linkedById.get(id) : undefined;
  };

  useEffect(() => {
    setHeatDraftByPoItem((prev) => {
      const next: Record<string, HeatDraftRow[]> = { ...prev };
      for (const l of receivableLines) {
        if (!selectedLineIds.has(l.id)) {
          delete next[l.id];
          continue;
        }
        const linkedId = stockIdForCreateLine(l, stockItemByLine);
        if (!grnLineNeedsHeat(l, linkedId ? linkedById.get(linkedId) : undefined)) {
          delete next[l.id];
          continue;
        }
        const acc = lineQuantities[l.id]?.accepted ?? 0;
        const cur = next[l.id];
        if (!cur || cur.length === 0) {
          next[l.id] = [{ heat_number: '', dimension: '', quantity: acc }];
        } else if (cur.length === 1) {
          next[l.id] = [{ ...cur[0], quantity: acc }];
        }
      }
      return next;
    });
    }, [receivableLines, selectedLineIds, lineQuantities, stockItemByLine, linkedById]);

  useEffect(() => {
    const distinctProjectIds = [
      ...new Set(
        receivableLines.map((l) => l.project_id).filter((id): id is string => Boolean(id))
      ),
    ];
    if (distinctProjectIds.length === 1) {
      form.setFieldValue('project_id', distinctProjectIds[0]);
    } else if (distinctProjectIds.length === 0 && selectedPo?.project_id) {
      form.setFieldValue('project_id', selectedPo.project_id);
    } else if (distinctProjectIds.length !== 1) {
      form.setFieldValue('project_id', undefined);
    }
    if (selectedPo?.department_id) {
      form.setFieldValue('department_id', selectedPo.department_id);
    }
  }, [receivableLines, selectedPo?.project_id, selectedPo?.department_id, form]);

  const lineProjectHint = useMemo(
    () => headerProjectLabel(selectedPo ?? null, receivableLines),
    [selectedPo, receivableLines]
  );

  const isMultiProjectLines = useMemo(() => {
    const ids = new Set(
      receivableLines.map((l) => l.project_id).filter((id): id is string => Boolean(id))
    );
    return ids.size > 1;
  }, [receivableLines]);

  const updateHeatDraft = (poItemId: string, index: number, patch: Partial<HeatDraftRow>) => {
    setHeatDraftByPoItem((prev) => {
      const rows = [...(prev[poItemId] || [])];
      if (!rows[index]) return prev;
      rows[index] = { ...rows[index], ...patch };
      return { ...prev, [poItemId]: rows };
    });
  };

  const addHeatRow = (poItemId: string) => {
    setHeatDraftByPoItem((prev) => ({
      ...prev,
      [poItemId]: [
        ...(prev[poItemId] || []),
        { heat_number: '', dimension: '', quantity: 0, mtc_document_id: null },
      ],
    }));
  };

  const removeHeatRow = (poItemId: string, index: number) => {
    setHeatDraftByPoItem((prev) => {
      const rows = [...(prev[poItemId] || [])];
      rows.splice(index, 1);
      return { ...prev, [poItemId]: rows };
    });
  };

  const toggleLine = (lineId: string) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
    setLineQuantities((prev) => {
      const next = { ...prev };
      if (!selectedLineIds.has(lineId)) {
        const line = receivableLines.find((l) => l.id === lineId);
        if (line) next[lineId] = { received: line.pending_qty, accepted: line.pending_qty, rejected: 0, hold: 0 };
      } else delete next[lineId];
      return next;
    });
  };

  const setQuantity = (lineId: string, field: 'received' | 'accepted' | 'rejected' | 'hold', value: number) => {
    setLineQuantities((prev) => {
      const current = prev[lineId] ?? { received: 0, accepted: 0, rejected: 0, hold: 0 };
      const next = { ...current, [field]: value };
      if (field === 'received') {
        next.accepted = Math.min(next.accepted, value);
        next.rejected = Math.min(next.rejected, value);
        next.hold = Math.min(next.hold, value);
        if (next.accepted + next.rejected + next.hold !== value) {
          next.accepted = value;
          next.rejected = 0;
          next.hold = 0;
        }
      }
      return { ...prev, [lineId]: next };
    });
  };

  const handleSubmit = async () => {
    if (!poId || selectedLines.length === 0) {
      message.warning('Select a PO and at least one line');
      return;
    }
    if (isMilestonePo && !milestoneId) {
      message.warning('Select a GRN milestone for this PO');
      return;
    }
    const grnDate = form.getFieldValue('grn_date');
    const receivedBy = form.getFieldValue('received_by');
    const warehouseId = form.getFieldValue('warehouse_id');
    const departmentId = form.getFieldValue('department_id');
    if (!grnDate || !receivedBy) {
      message.warning('GRN date and Received by are required');
      return;
    }
    if (!warehouseId) {
      message.warning('Warehouse is required');
      return;
    }
    if (!departmentId) {
      message.warning('Department is required');
      return;
    }
    const lines = selectedLines.map((l) => {
      const q = lineQuantities[l.id] ?? { received: l.pending_qty, accepted: l.pending_qty, rejected: 0, hold: 0 };
      const linked = linkedItemFor(l);
      const heat_lines = grnLineNeedsHeat(l, linked)
        ? (heatDraftByPoItem[l.id] || []).map((h) => ({
            heat_number: String(h.heat_number ?? '').trim(),
            dimension: String(h.dimension ?? '').trim(),
            quantity: Number(h.quantity) || 0,
            mtc_document_id: h.mtc_document_id?.trim() || null,
          }))
        : undefined;
      return {
        po_item_id: l.id,
        received_quantity: q.received,
        accepted_quantity: q.accepted,
        rejected_quantity: q.rejected,
        hold_quantity: q.hold,
        stock_item_id:
          l.item_type === 'Non-Stock'
            ? Object.prototype.hasOwnProperty.call(stockItemByLine, l.id)
              ? stockItemByLine[l.id] || null
              : l.default_stock_item_id || null
            : null,
        heat_lines,
        ...(actualWeightByLine[l.id] != null ? { actual_weight: actualWeightByLine[l.id], actual_weight_uom: 'KG' } : {}),
      };
    });
    const invalid = lines.find((l) => l.received_quantity <= 0 || l.accepted_quantity + l.rejected_quantity + l.hold_quantity !== l.received_quantity);
    if (invalid) {
      message.warning('Each line must have received > 0 and accepted + rejected + hold = received');
      return;
    }
    for (let i = 0; i < selectedLines.length; i++) {
      const l = selectedLines[i];
      const linked = linkedItemFor(l);
      const traceCode = grnTraceItemCode(l, linked) || String(l.line_number);
      if (!grnLineNeedsHeat(l, linked)) continue;
      const q = lineQuantities[l.id] ?? { received: 0, accepted: 0, rejected: 0, hold: 0 };
      const hl = heatDraftByPoItem[l.id] || [];
      if (hl.length === 0) {
        message.warning(`Add at least one heat line for critical item ${traceCode}`);
        return;
      }
      const sum = hl.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
      if (Math.abs(sum - q.accepted) > 0.0001) {
        message.warning(
          `Heat quantities must sum to accepted quantity (${q.accepted}) for item ${traceCode}`
        );
        return;
      }
      if (hl.some((r) => !String(r.heat_number ?? '').trim())) {
        message.warning(`Enter a heat number for each heat line on item ${traceCode}`);
        return;
      }
      if (hl.some((r) => !String(r.dimension ?? '').trim())) {
        message.warning(`Enter a dimension for each heat line on item ${traceCode}`);
        return;
      }
      if (grnLineNeedsMtc(l, linked) && hl.some((r) => !String(r.mtc_document_id ?? '').trim())) {
        message.warning(
          `MTC document is required for each heat line on item ${traceCode}. Upload MTC or enter document UUID / document number.`
        );
        return;
      }
    }
    const dto: CreateGRNDTO = {
      po_id: poId,
      po_milestone_id: milestoneId ?? undefined,
      grn_date: grnDate,
      receipt_time: form.getFieldValue('receipt_time'),
      supplier_delivery_note: form.getFieldValue('supplier_delivery_note'),
      supplier_invoice_number: form.getFieldValue('supplier_invoice_number'),
      vehicle_number: form.getFieldValue('vehicle_number'),
      driver_name: form.getFieldValue('driver_name'),
      warehouse_id: warehouseId,
      warehouse_location: form.getFieldValue('warehouse_location'),
      received_by: receivedBy,
      project_id: isMultiProjectLines ? null : form.getFieldValue('project_id') ?? null,
      department_id: form.getFieldValue('department_id'),
      notes: form.getFieldValue('notes'),
      lines,
    };
    setSubmitLoading(true);
    try {
      const grn = await goodsReceiptNoteService.create(dto);
      message.success('GRN created');
      navigate(`/scm/procurement/grn/${grn.id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to create GRN');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/grn')} style={{ marginBottom: 16 }}>
        Back to GRN list
      </Button>
      <Title level={4} style={{ marginBottom: 24 }}>
        Create Goods Receipt Note
      </Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          grn_date: new Date().toISOString().slice(0, 10),
          received_by: user?.id ?? '',
        }}
      >
        <Card title="1. Select Purchase Order" style={{ marginBottom: 16 }}>
          <Form.Item label="Purchase Order" required>
            <Select
              placeholder="Search PO number or supplier"
              style={{ width: '100%', maxWidth: 480 }}
              value={poId}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              onChange={(v) => {
                setPoId(v);
                setMilestoneId(null);
                setSelectedLineIds(new Set());
                setLineQuantities({});
              }}
              options={receivablePOs.map((po) => ({
                value: po.id,
                label: `${po.po_number} – ${po.supplier_name} (${po.po_date})`,
              }))}
            />
          </Form.Item>
          {isMilestonePo && (
            <Form.Item label="Material milestone" required>
              <Select
                placeholder="Select GRN milestone"
                value={milestoneId ?? undefined}
                onChange={setMilestoneId}
                options={grnMilestones.map((m) => ({
                  value: m.id,
                  label: `#${m.milestone_number} ${m.milestone_name} (${Number(m.milestone_amount).toFixed(2)}) — ${m.status}`,
                }))}
              />
              {selectedMilestone && (
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  Balance: {Number(selectedMilestone.balance_value ?? selectedMilestone.milestone_amount).toFixed(2)}
                </Text>
              )}
            </Form.Item>
          )}
        </Card>

        {poId && (
          <Card title="2. Select lines and quantities" style={{ marginBottom: 16 }}>
            <Form.Item
              name="warehouse_id"
              label="Warehouse"
              rules={[{ required: true, message: 'Warehouse is required' }]}
            >
              <Select
                placeholder="Select warehouse"
                style={{ width: '100%', maxWidth: 300 }}
                options={warehouseOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item label="GRN Date" name="grn_date" rules={[{ required: true }]}>
              <Input type="date" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item label="Received by" name="received_by" rules={[{ required: true, message: 'Received by is required' }]}>
              <Select
                placeholder="Select user"
                style={{ width: '100%', maxWidth: 360 }}
                options={userOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="project_id"
              label={isMultiProjectLines ? 'Project (optional — line projects used for accounting)' : 'Project'}
              extra={hasMultipleLineProjects(receivableLines) ? 'Line projects differ; header project is optional metadata only.' : undefined}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={isMultiProjectLines ? 'Optional — line projects used for accounting' : 'Select project'}
                style={{ width: '100%', maxWidth: 400 }}
                options={projects.map((p: { id: string; project_name?: string; project_number?: string }) => ({
                  value: p.id,
                  label: p.project_name ?? p.project_number ?? p.id,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
            >
              <DepartmentSelect style={{ width: '100%', maxWidth: 400 }} />
            </Form.Item>
            <Form.Item label="Supplier delivery note" name="supplier_delivery_note">
              <Input style={{ maxWidth: 300 }} />
            </Form.Item>
            <Form.Item label="Vehicle number" name="vehicle_number">
              <Input style={{ maxWidth: 200 }} />
            </Form.Item>
            <Form.Item label="Notes" name="notes">
              <Input.TextArea rows={2} style={{ maxWidth: 400 }} />
            </Form.Item>

            <Table
              loading={linesLoading}
              size="small"
              rowKey="id"
              pagination={false}
              scroll={{ x: 900 }}
              dataSource={receivableLines}
              rowClassName={(r) =>
                getCriticalItemRowClassName({
                  is_critical: Boolean(r.is_critical) || Boolean(linkedItemFor(r)?.is_critical),
                })
              }
              rowSelection={{
                selectedRowKeys: Array.from(selectedLineIds),
                onChange: (keys) => {
                  const set = new Set(keys as string[]);
                  setSelectedLineIds(set);
                  setLineQuantities((prev) => {
                    const next = { ...prev };
                    receivableLines.forEach((l) => {
                      if (set.has(l.id) && !prev[l.id]) next[l.id] = { received: l.pending_qty, accepted: l.pending_qty, rejected: 0, hold: 0 };
                      if (!set.has(l.id)) delete next[l.id];
                    });
                    return next;
                  });
                },
              }}
              columns={[
                { title: 'Line', dataIndex: 'line_number', width: 60 },
                {
                  title: 'Item',
                  dataIndex: 'item_code',
                  width: 140,
                  render: (code: string, r: ReceivablePOLineDTO) => (
                    <Space size={6} wrap>
                      <span>{code}</span>
                      <CriticalItemTag
                        critical={isItemCritical(r) || isItemCritical(linkedItemFor(r))}
                      />
                    </Space>
                  ),
                },
                {
                  title: 'Description',
                  dataIndex: 'item_description',
                  render: (desc: string, r: ReceivablePOLineDTO) => (
                    <>
                      <div>{desc}</div>
                      <LineSpecificationsText value={r.specifications} />
                      {r.stock_quantity != null &&
                      !(Object.prototype.hasOwnProperty.call(stockItemByLine, r.id)
                        ? stockItemByLine[r.id]
                        : r.default_stock_item_id) ? (
                        <div style={{ fontSize: 12, color: '#888' }}>
                          Stock in: {Number(r.stock_quantity)} {r.stock_uom || ''}
                        </div>
                      ) : null}
                    </>
                  ),
                },
                {
                  title: 'Supplier Description',
                  dataIndex: 'supplier_description',
                  render: (v: string, r: ReceivablePOLineDTO) => v || r.item_description || '—',
                },
                {
                  title: 'Inventory item',
                  width: 220,
                  render: (_: unknown, r: ReceivablePOLineDTO) =>
                    r.item_type === 'Non-Stock' ? (
                      <ItemSelect
                        allowClear
                        allowedItemTypes={['Stock', 'Consumable']}
                        placeholder="Link a stock item"
                        value={
                          Object.prototype.hasOwnProperty.call(stockItemByLine, r.id)
                            ? stockItemByLine[r.id] ?? undefined
                            : r.default_stock_item_id ?? undefined
                        }
                        onChange={(v, option) => {
                          setStockItemByLine((prev) => ({ ...prev, [r.id]: v ? String(v) : null }));
                          const uom = v ? inventoryUomFromSelectOption(option) : null;
                          setLinkedUomByLine((prev) => {
                            const next = { ...prev };
                            if (uom) next[r.id] = uom;
                            else delete next[r.id];
                            return next;
                          });
                        }}
                      />
                    ) : (
                      '—'
                    ),
                },
                {
                  title: 'Project',
                  key: 'project',
                  width: 120,
                  ellipsis: true,
                  render: (_, r: ReceivablePOLineDTO) => projectLabel(r),
                },
                { title: 'Ordered', dataIndex: 'ordered_quantity', width: 80 },
                {
                  title: 'Posted',
                  dataIndex: 'received_quantity',
                  width: 80,
                  render: (v: number) => v ?? 0,
                },
                {
                  title: 'On open GRNs',
                  dataIndex: 'in_process_quantity',
                  width: 100,
                  render: (v: number | undefined) => v ?? 0,
                },
                { title: 'Pending', dataIndex: 'pending_qty', width: 80 },
                {
                  title: 'UOM',
                  dataIndex: 'uom',
                  width: 60,
                  render: (uom: string, r: ReceivablePOLineDTO) => linkedUomByLine[r.id] || uom,
                },
                {
                  title: 'Received qty',
                  key: 'received',
                  width: 110,
                  render: (_, r: ReceivablePOLineDTO) =>
                    selectedLineIds.has(r.id) ? (
                      <InputNumber
                        min={0}
                        max={r.pending_qty}
                        value={lineQuantities[r.id]?.received ?? r.pending_qty}
                        onChange={(v) => setQuantity(r.id, 'received', Number(v) || 0)}
                        style={{ width: 90 }}
                      />
                    ) : null,
                },
                {
                  title: 'Accepted',
                  key: 'accepted',
                  width: 90,
                  render: (_, r: ReceivablePOLineDTO) =>
                    selectedLineIds.has(r.id) ? (
                      <InputNumber
                        min={0}
                        max={lineQuantities[r.id]?.received ?? r.pending_qty}
                        value={lineQuantities[r.id]?.accepted ?? 0}
                        onChange={(v) => setQuantity(r.id, 'accepted', Number(v) || 0)}
                        style={{ width: 70 }}
                      />
                    ) : null,
                },
                {
                  title: 'Rejected',
                  key: 'rejected',
                  width: 80,
                  render: (_, r: ReceivablePOLineDTO) =>
                    selectedLineIds.has(r.id) ? (
                      <InputNumber
                        min={0}
                        value={lineQuantities[r.id]?.rejected ?? 0}
                        onChange={(v) => setQuantity(r.id, 'rejected', Number(v) || 0)}
                        style={{ width: 70 }}
                      />
                    ) : null,
                },
                {
                  title: 'Hold',
                  key: 'hold',
                  width: 70,
                  render: (_, r: ReceivablePOLineDTO) =>
                    selectedLineIds.has(r.id) ? (
                      <InputNumber
                        min={0}
                        value={lineQuantities[r.id]?.hold ?? 0}
                        onChange={(v) => setQuantity(r.id, 'hold', Number(v) || 0)}
                        style={{ width: 60 }}
                      />
                    ) : null,
                },
                ...(receivableLines.some((l) => l.theoretical_weight != null)
                  ? [
                      {
                        title: 'Actual weight (kg)',
                        key: 'actual_weight',
                        width: 170,
                        render: (_: unknown, r: ReceivablePOLineDTO) =>
                          selectedLineIds.has(r.id) && r.theoretical_weight != null ? (
                            <GrnActualWeightInput
                              expected={expectedReceiptWeight(
                                r.theoretical_weight,
                                lineQuantities[r.id]?.received ?? r.pending_qty,
                                r.ordered_quantity
                              )}
                              actual={actualWeightByLine[r.id]}
                              tolerancePlusPct={r.weight_tolerance_plus_pct}
                              toleranceMinusPct={r.weight_tolerance_minus_pct}
                              onChange={(v) => setActualWeightByLine((prev) => ({ ...prev, [r.id]: v }))}
                            />
                          ) : null,
                      },
                    ]
                  : []),
              ]}
            />

            {selectedLines.some((l) => Boolean(l.is_critical) || Boolean(linkedItemFor(l)?.is_critical)) && (
              <Alert
                style={{ marginTop: 16 }}
                type="info"
                showIcon
                message="Critical items require QC before posting"
                description="After this GRN is approved, quality must Pass inspection before stock/GL post or supplier invoice."
              />
            )}

            {selectedLines.some((l) => grnLineNeedsHeat(l, linkedItemFor(l))) && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Heat splits (critical items)</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  For each heat, enter heat number, dimension, and quantity. Attach an MTC document per heat using Upload MTC (stored on the PO line until the GRN is created) or enter the document UUID or document number from Documents. Items flagged “MTC required on receipt” must have a document on every heat line before you can create the GRN.
                </Text>
                {selectedLines.filter((l) => grnLineNeedsHeat(l, linkedItemFor(l))).map((l) => {
                  const linked = linkedItemFor(l);
                  const rows = heatDraftByPoItem[l.id] || [
                    { heat_number: '', dimension: '', quantity: lineQuantities[l.id]?.accepted ?? 0 },
                  ];
                  const traceCode = grnTraceItemCode(l, linked);
                  return (
                    <Card key={l.id} size="small" style={{ marginBottom: 12 }} title={`${traceCode || l.item_code || 'Line'} — ${l.item_description}`}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {rows.map((row, idx) => (
                          <div
                            key={idx}
                            style={{
                              borderBottom: '1px solid #f0f0f0',
                              paddingBottom: 12,
                              marginBottom: 8,
                              width: '100%',
                            }}
                          >
                            <Space wrap style={{ marginBottom: 8 }}>
                              <Input
                                placeholder="Heat number"
                                style={{ width: 160 }}
                                value={row.heat_number}
                                onChange={(e) => updateHeatDraft(l.id, idx, { heat_number: e.target.value })}
                              />
                              <Input
                                placeholder="Dimension"
                                style={{ width: 160 }}
                                value={row.dimension}
                                onChange={(e) => updateHeatDraft(l.id, idx, { dimension: e.target.value })}
                              />
                              <InputNumber
                                min={0}
                                placeholder="Qty"
                                value={row.quantity}
                                onChange={(v) => updateHeatDraft(l.id, idx, { quantity: Number(v) || 0 })}
                                style={{ width: 100 }}
                              />
                              <Input
                                placeholder="Document UUID or number"
                                style={{ width: 220 }}
                                value={row.mtc_document_id ?? ''}
                                onChange={(e) =>
                                  updateHeatDraft(l.id, idx, {
                                    mtc_document_id: e.target.value.trim() || null,
                                  })
                                }
                              />
                              {rows.length > 1 && (
                                <Button type="link" danger onClick={() => removeHeatRow(l.id, idx)}>
                                  Remove
                                </Button>
                              )}
                            </Space>
                            <Space wrap align="center">
                              <DocumentUpload
                                entityType="PurchaseOrderItem"
                                entityId={l.id}
                                buttonText="Upload MTC"
                                onSuccess={(doc) => {
                                  if (doc?.id) updateHeatDraft(l.id, idx, { mtc_document_id: doc.id });
                                }}
                              />
                              {grnLineNeedsMtc(l, linked) ? (
                                <Text type="secondary">MTC required for this item.</Text>
                              ) : null}
                            </Space>
                          </div>
                        ))}
                        <Button type="dashed" size="small" onClick={() => addHeatRow(l.id)}>
                          Add heat line
                        </Button>
                      </Space>
                    </Card>
                  );
                })}
              </div>
            )}
          </Card>
        )}

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" onClick={handleSubmit} loading={submitLoading} disabled={!poId || selectedLines.length === 0}>
            Create GRN
          </Button>
          <Button onClick={() => navigate('/scm/procurement/grn')}>Cancel</Button>
        </Space>
      </Form>
    </div>
  );
};

export default CreateGRNPage;
